import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Execution, Flow } from '../types/api';

/**
 * UI state for workflow list filters
 */
interface WorkflowFilters {
  search: string;
  selectedFolder: string;
  selectedTags: string[];
  status: Flow['status'] | '';
  showFilters: boolean;
}

/**
 * UI state for execution list filters
 */
interface ExecutionFilters {
  search: string;
  status: Execution['status'] | '';
  flowId: string;
  dateRange: {
    start: string;
    end: string;
  };
  showFilters: boolean;
}

/**
 * Global UI preferences
 */
interface UIPreferences {
  theme: 'light' | 'dark';
  itemsPerPage: number;
  autoRefresh: boolean;
  compactMode: boolean;
}

/**
 * Global UI state interface
 */
interface UIState {
  // Workflow filters
  workflowFilters: WorkflowFilters;
  setWorkflowFilters: (filters: Partial<WorkflowFilters>) => void;
  resetWorkflowFilters: () => void;

  // Execution filters
  executionFilters: ExecutionFilters;
  setExecutionFilters: (filters: Partial<ExecutionFilters>) => void;
  resetExecutionFilters: () => void;

  // UI preferences
  preferences: UIPreferences;
  setPreferences: (prefs: Partial<UIPreferences>) => void;

  // Modal states
  isCreateFlowModalOpen: boolean;
  setCreateFlowModalOpen: (open: boolean) => void;
  
  isCreateFolderModalOpen: boolean;
  setCreateFolderModalOpen: (open: boolean) => void;

  // Loading states for global operations
  globalLoading: {
    [key: string]: boolean;
  };
  setGlobalLoading: (key: string, loading: boolean) => void;
}

/**
 * Default workflow filters
 */
const defaultWorkflowFilters: WorkflowFilters = {
  search: '',
  selectedFolder: '',
  selectedTags: [],
  status: '',
  showFilters: false,
};

/**
 * Default execution filters
 */
const defaultExecutionFilters: ExecutionFilters = {
  search: '',
  status: '',
  flowId: '',
  dateRange: {
    start: '',
    end: '',
  },
  showFilters: false,
};

/**
 * Default UI preferences
 */
const defaultPreferences: UIPreferences = {
  theme: 'light',
  itemsPerPage: 20,
  autoRefresh: true,
  compactMode: false,
};

/**
 * Zustand store for global UI state
 */
export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Workflow filters
      workflowFilters: defaultWorkflowFilters,
      setWorkflowFilters: (filters) =>
        set((state) => ({
          workflowFilters: { ...state.workflowFilters, ...filters },
        })),
      resetWorkflowFilters: () =>
        set({ workflowFilters: defaultWorkflowFilters }),

      // Execution filters
      executionFilters: defaultExecutionFilters,
      setExecutionFilters: (filters) =>
        set((state) => ({
          executionFilters: { ...state.executionFilters, ...filters },
        })),
      resetExecutionFilters: () =>
        set({ executionFilters: defaultExecutionFilters }),

      // UI preferences
      preferences: defaultPreferences,
      setPreferences: (prefs) =>
        set((state) => ({
          preferences: { ...state.preferences, ...prefs },
        })),

      // Modal states (not persisted)
      isCreateFlowModalOpen: false,
      setCreateFlowModalOpen: (open) => set({ isCreateFlowModalOpen: open }),
      
      isCreateFolderModalOpen: false,
      setCreateFolderModalOpen: (open) => set({ isCreateFolderModalOpen: open }),

      // Global loading states (not persisted)
      globalLoading: {},
      setGlobalLoading: (key, loading) =>
        set((state) => ({
          globalLoading: { ...state.globalLoading, [key]: loading },
        })),
    }),
    {
      name: 'workflow-ui-store',
      // Only persist certain parts of the state
      partialize: (state) => ({
        workflowFilters: state.workflowFilters,
        executionFilters: state.executionFilters,
        preferences: state.preferences,
      }),
    }
  )
);

/**
 * Selector hooks for specific parts of the store
 */
export const useWorkflowFilters = () => useUIStore((state) => state.workflowFilters);
export const useExecutionFilters = () => useUIStore((state) => state.executionFilters);
export const useUIPreferences = () => useUIStore((state) => state.preferences); 